/**
 * waypoints.ts — shared client helpers for personal map markers.
 *
 * PLANET-AWARE: `myWaypoints` returns only the rows stamped with the world
 * the player currently stands in — a marker set on another planet never
 * reaches a map here. Creation goes through `createWaypoint`, whose world
 * is the SERVER's view of where you are (never a client argument).
 */
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { trackKey } from './waypoint_hud.js';

export interface WaypointRow {
  waypointId: bigint;
  owner: { isEqual(o: unknown): boolean };
  worldId: bigint;
  name: string;
  posX: number;
  posZ: number;
}

export const WAYPOINT_COLOR = '#ffd166';

/** The player's own waypoints ON THE CURRENT WORLD (the planet-aware filter). */
export function myWaypoints(conn: DbConnection | null): WaypointRow[] {
  const out: WaypointRow[] = [];
  if (!conn?.identity || atlas.currentWorldId === null) return out;
  try {
    for (const w of conn.db.waypoint.iter()) {
      if (w.worldId === atlas.currentWorldId && w.owner.isEqual(conn.identity)) out.push(w as unknown as WaypointRow);
    }
  } catch { /* row cache mid-churn */ }
  return out;
}

/** Create at (wx, wz): prompts for a name; empty/cancel aborts. */
export function promptCreateWaypoint(conn: DbConnection, wx: number, wz: number): void {
  const name = window.prompt(`Name this waypoint (${Math.round(wx)}, ${Math.round(wz)})`, 'Waypoint');
  if (name === null || name.trim() === '') return;
  conn.reducers.createWaypoint({ name: name.trim().slice(0, 32), posX: wx, posZ: wz })
    .then(() => bus.emit('atlas:status', { text: `Waypoint “${name.trim().slice(0, 32)}” set` }))
    .catch((e: Error) => bus.emit('atlas:status', { text: `Waypoint refused: ${e.message}` }));
}

/** Edit an existing marker: rename, or clear the text to delete it. */
export function promptEditWaypoint(conn: DbConnection, w: WaypointRow): void {
  const next = window.prompt(`Rename “${w.name}” — or CLEAR the text to delete it`, w.name);
  if (next === null) return;
  if (next.trim() === '') {
    conn.reducers.deleteWaypoint({ waypointId: w.waypointId })
      .then(() => bus.emit('atlas:status', { text: `Waypoint “${w.name}” deleted` }))
      .catch((e: Error) => bus.emit('atlas:status', { text: `Delete refused: ${e.message}` }));
    return;
  }
  if (next.trim() !== w.name) {
    conn.reducers.renameWaypoint({ waypointId: w.waypointId, name: next.trim().slice(0, 32) })
      .catch((e: Error) => bus.emit('atlas:status', { text: `Rename refused: ${e.message}` }));
  }
}

/** The followed-waypoint choice per world, read from localStorage ONCE and kept in memory with write-through
 *  (2026-09-25): the HUD read it every frame and the World Atlas on every dirty frame — a synchronous storage read on
 *  the frame. Another tab's write arrives as a `storage` event and drops the cached copy. */
const trackCache = new Map<string, string>();
let trackListening = false;
export function readTrackChoice(worldId: bigint | string): string {
  const k = trackKey(worldId);
  const hit = trackCache.get(k);
  if (hit !== undefined) return hit;
  if (!trackListening && typeof window !== 'undefined') {
    trackListening = true;
    window.addEventListener('storage', (e) => { if (e.key === null) trackCache.clear(); else if (e.key.startsWith('atlas:waypoint-track:')) trackCache.delete(e.key); });
  }
  let v = '';
  try { v = localStorage.getItem(k) ?? ''; } catch { /* private window: no choice */ }
  trackCache.set(k, v);
  return v;
}
export function writeTrackChoice(worldId: bigint | string, v: string): void {
  const k = trackKey(worldId);
  trackCache.set(k, v);
  try { if (v === '') localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* private window: memory only */ }
}

/** The waypoint the in-world indicator follows on THIS world ('' = the newest, 'off', or an id) — see waypoint_hud.ts. */
export function trackedWaypointChoice(): string {
  if (atlas.currentWorldId === null) return '';
  return readTrackChoice(atlas.currentWorldId); // cached (2026-09-25): the map draws called this per dirty frame
}

/** The waypoint the indicator is following right now (the HUD's own resolution), or null. */
export function trackedWaypoint(conn: DbConnection | null): WaypointRow | null {
  const rows = myWaypoints(conn);
  if (rows.length === 0) return null;
  const choice = trackedWaypointChoice();
  if (choice === 'off') return null;
  let newest = rows[0];
  for (const r of rows) if (r.waypointId > newest.waypointId) newest = r;
  return (choice !== '' ? rows.find((r) => String(r.waypointId) === choice) : undefined) ?? newest;
}

/** Follow this waypoint with the in-world indicator (null stops following). */
export function trackWaypoint(id: bigint | null): void {
  bus.emit('atlas:waypoint-track', { id: id === null ? null : String(id) });
}

/** The waypoint marker within `radius` px of (px, py) on a map, nearest first. */
export function waypointAt(rows: readonly WaypointRow[], toX: (x: number) => number, toY: (z: number) => number, px: number, py: number, radius = 12): WaypointRow | null {
  let hit: WaypointRow | null = null;
  let best = radius;
  for (const w of rows) {
    const d = Math.hypot(toX(w.posX) - px, toY(w.posZ) - py);
    if (d < best) { best = d; hit = w; }
  }
  return hit;
}

/** Draw the shared marker glyph: a gold diamond + name tag. */
export function drawWaypointMarker(g: CanvasRenderingContext2D, x: number, y: number, name: string, dim = false, tracked = false): void {
  g.save();
  g.globalAlpha = dim ? 0.55 : 1;
  if (tracked) { // the one the in-world indicator follows: a ring around the diamond
    g.strokeStyle = WAYPOINT_COLOR;
    g.lineWidth = 1.5;
    g.beginPath(); g.arc(x, y, 10, 0, Math.PI * 2); g.stroke();
  }
  g.fillStyle = WAYPOINT_COLOR;
  g.strokeStyle = '#0a0f18';
  g.lineWidth = 1;
  g.beginPath();
  g.moveTo(x, y - 6); g.lineTo(x + 5, y); g.lineTo(x, y + 6); g.lineTo(x - 5, y);
  g.closePath();
  g.fill();
  g.stroke();
  if (name) {
    g.font = '10px system-ui';
    const tw = g.measureText(name).width;
    g.fillStyle = 'rgba(8,12,20,.75)';
    g.fillRect(x + 7, y - 7, tw + 6, 14);
    g.fillStyle = WAYPOINT_COLOR;
    g.fillText(name, x + 10, y + 4);
  }
  g.restore();
}
